/**
 * Legal notice page (Mentions légales).
 *
 * Displays legally required information for French public websites
 * as per Article 6 of LCEN (Loi pour la Confiance dans l'Économie Numérique).
 *
 * IMPORTANT: This page contains placeholder values that MUST be customized
 * for each deployment. See docs/operations/legal-configuration.md.
 */

import { Link } from "react-router-dom";

/**
 * Configuration for legal notice content.
 * These values should come from environment variables or CMS in production.
 */
const LEGAL_CONFIG = {
  // Éditeur du site
  editor: {
    name: "[Nom de la collectivité]",
    type: "Établissement public",
    address: "[Adresse de la collectivité]",
    postalCode: "[Code postal]",
    city: "[Ville]",
    phone: "[Numéro de téléphone]",
    email: "[Email de contact]",
    siret: "[Numéro SIRET]",
  },
  // Directeur de la publication
  publicationDirector: {
    name: "[Nom du directeur de publication]",
    title: "[Fonction]",
  },
  // Hébergeur
  hosting: {
    name: "[Nom de l'hébergeur]",
    address: "[Adresse de l'hébergeur]",
    phone: "[Téléphone de l'hébergeur]",
  },
  // DPO
  dpo: {
    name: "[Nom du DPO ou service]",
    email: "[Email du DPO]",
    address: "[Adresse pour exercice des droits]",
  },
};

export function LegalNoticePage() {
  return (
    <div className="mx-auto max-w-4xl">
      <nav aria-label="Fil d'Ariane" className="mb-6">
        <ol className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <li>
            <Link
              to="/opac"
              className="hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none dark:hover:text-blue-400"
            >
              Accueil
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li aria-current="page" className="font-medium text-gray-900 dark:text-white">
            Mentions légales
          </li>
        </ol>
      </nav>

      <article className="prose prose-gray dark:prose-invert max-w-none">
        <h1>Mentions légales</h1>

        <p className="lead">
          Conformément aux dispositions de la loi n° 2004-575 du 21 juin 2004 pour la confiance dans
          l'économie numérique, il est précisé aux utilisateurs du site l'identité des différents
          intervenants dans le cadre de sa réalisation et de son suivi.
        </p>

        <section>
          <h2>Éditeur du site</h2>
          <p>Le présent site est édité par :</p>
          <address className="not-italic">
            <strong>{LEGAL_CONFIG.editor.name}</strong>
            <br />
            {LEGAL_CONFIG.editor.type}
            <br />
            {LEGAL_CONFIG.editor.address}
            <br />
            {LEGAL_CONFIG.editor.postalCode} {LEGAL_CONFIG.editor.city}
            <br />
            Téléphone : {LEGAL_CONFIG.editor.phone}
            <br />
            Email : <a href={`mailto:${LEGAL_CONFIG.editor.email}`}>{LEGAL_CONFIG.editor.email}</a>
            <br />
            SIRET : {LEGAL_CONFIG.editor.siret}
          </address>
        </section>

        <section>
          <h2>Directeur de la publication</h2>
          <p>
            <strong>{LEGAL_CONFIG.publicationDirector.name}</strong>
            <br />
            {LEGAL_CONFIG.publicationDirector.title}
          </p>
        </section>

        <section>
          <h2>Hébergement</h2>
          <p>Le site est hébergé par :</p>
          <address className="not-italic">
            <strong>{LEGAL_CONFIG.hosting.name}</strong>
            <br />
            {LEGAL_CONFIG.hosting.address}
            <br />
            Téléphone : {LEGAL_CONFIG.hosting.phone}
          </address>
        </section>

        <section>
          <h2>Propriété intellectuelle</h2>
          <p>
            L'ensemble des éléments constituant ce site (textes, graphismes, logiciels,
            photographies, images, sons, plans, logos, marques, créations et œuvres protégeables
            diverses, bases de données, etc.) ainsi que le site lui-même, relèvent des législations
            françaises et internationales sur le droit d'auteur et la propriété intellectuelle.
          </p>
          <p>
            Ces éléments sont la propriété exclusive de {LEGAL_CONFIG.editor.name}. Toute
            reproduction, représentation, modification, publication, transmission, ou plus
            généralement toute exploitation non autorisée du site ou de l'un quelconque des éléments
            qu'il contient, engage la responsabilité de l'utilisateur et constitue une contrefaçon
            sanctionnée par le Code de la Propriété Intellectuelle.
          </p>
          <p>
            Les notices bibliographiques sont diffusées sous licence ouverte dans le respect des
            droits des auteurs et éditeurs.
          </p>
        </section>

        <section>
          <h2>Données personnelles</h2>
          <p>
            Les informations recueillies via ce site font l'objet d'un traitement informatique
            destiné à la gestion des inscriptions et des prêts de la bibliothèque. Les données sont
            conservées conformément à la réglementation en vigueur.
          </p>
          <p>
            Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi
            Informatique et Libertés, vous disposez d'un droit d'accès, de rectification, de
            suppression et de portabilité de vos données, ainsi que d'un droit d'opposition et de
            limitation du traitement.
          </p>
          <p>
            Pour exercer ces droits ou pour toute question relative au traitement de vos données,
            vous pouvez contacter :
          </p>
          <address className="not-italic">
            <strong>{LEGAL_CONFIG.dpo.name}</strong>
            <br />
            Email : <a href={`mailto:${LEGAL_CONFIG.dpo.email}`}>{LEGAL_CONFIG.dpo.email}</a>
            <br />
            Adresse : {LEGAL_CONFIG.dpo.address}
          </address>
          <p>
            Pour plus d'informations sur le traitement de vos données personnelles, consultez notre{" "}
            <Link to="/opac/confidentialite">Politique de confidentialité</Link>.
          </p>
          <p>
            Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés, vous
            pouvez introduire une réclamation auprès de la CNIL :{" "}
            <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">
              www.cnil.fr
            </a>
          </p>
        </section>

        <section>
          <h2>Cookies</h2>
          <p>
            Ce site utilise des cookies strictement nécessaires au fonctionnement du service
            (authentification, préférences de session). Ces cookies ne nécessitent pas votre
            consentement préalable car ils sont indispensables à la fourniture du service demandé.
          </p>
          <p>Aucun cookie de mesure d'audience ou de publicité n'est utilisé sur ce site.</p>
        </section>

        <section>
          <h2>Limitation de responsabilité</h2>
          <p>
            Les informations contenues sur ce site sont aussi précises que possible et le site est
            périodiquement mis à jour, mais peut toutefois contenir des inexactitudes, des omissions
            ou des lacunes.
          </p>
          <p>
            {LEGAL_CONFIG.editor.name} ne pourra être tenue responsable des dommages directs ou
            indirects résultant de l'accès au site ou de l'utilisation du site et/ou des
            informations qui y sont disponibles.
          </p>
        </section>

        <section>
          <h2>Droit applicable</h2>
          <p>
            Le présent site et les présentes mentions légales sont soumis au droit français. En cas
            de litige, les tribunaux français seront compétents.
          </p>
        </section>

        <section>
          <h2>Accessibilité</h2>
          <p>
            Ce site s'efforce d'être accessible à tous, conformément au Référentiel Général
            d'Amélioration de l'Accessibilité (RGAA). Pour toute difficulté rencontrée, vous pouvez
            nous contacter à l'adresse{" "}
            <a href={`mailto:${LEGAL_CONFIG.editor.email}`}>{LEGAL_CONFIG.editor.email}</a>.
          </p>
          <p>
            <Link to="/opac/accessibilite">Consulter la déclaration d'accessibilité</Link>
          </p>
        </section>

        <footer className="mt-8 border-t border-gray-200 pt-4 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          <p>Dernière mise à jour : {new Date().toLocaleDateString("fr-FR")}</p>
        </footer>
      </article>
    </div>
  );
}

export default LegalNoticePage;
